/**
 * OrdoSearch — the customer-facing navigation search palette.
 *
 * Replaces the former `@ordo/agent-embed` AgentTopbar in apps/ordo:
 * the Ordo agent (chat / "ask Agent" / suggested-question chips / the
 * purple ✦ sparkle) is removed for now. What remains is a plain
 * "JUMP TO" navigation search — the same profile-nav source the sidebar
 * reads — opened by the centered topbar pill, the ⌘K shortcut, and the
 * mobile search icon.
 *
 * State lives in a tiny module store so the mobile search button in the
 * topbar (a sibling in AppTopBarRight) can open the same palette the
 * centered pill owns.
 */
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@ordo/ui/command'
import { Dialog, DialogContent, DialogTitle } from '@ordo/ui/dialog'
import { Kbd } from '@ordo/ui/kbd'
import { Search } from 'lucide-react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import { useNavigate } from 'react-router'
import { create } from 'zustand'

import { isSearchShortcut } from '@/components/ordo-search-shortcut'
import { useProfile } from '@/stores/profile'

interface SearchOpenState {
  open: boolean
  setOpen: (open: boolean) => void
}

/** Shared open state so the mobile icon + ⌘K + the pill drive one palette. */
export const useSearchOpen = create<SearchOpenState>((set) => ({
  open: false,
  setOpen: (open) => set({ open }),
}))

/**
 * The centered search pill (desktop) plus the palette dialog. Kept
 * mounted on mobile via the parent's `md:contents` wrapper so ⌘K and
 * the mobile search icon still reach the dialog.
 */
export function OrdoSearch(): React.ReactElement {
  const intl = useIntl()
  const navigate = useNavigate()
  const { profile } = useProfile()
  const open = useSearchOpen((s) => s.open)
  const setOpen = useSearchOpen((s) => s.setOpen)

  const placeholder = intl.formatMessage({ id: 'search.placeholder' })
  const pages = profile?.nav.items ?? []
  const inputRef = React.useRef<HTMLInputElement>(null)

  // Global ⌘K / Ctrl+K toggle.
  React.useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (isSearchShortcut(e)) {
        e.preventDefault()
        setOpen(!useSearchOpen.getState().open)
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [setOpen])

  const go = React.useCallback(
    (href: string) => {
      setOpen(false)
      navigate(href)
    },
    [navigate, setOpen],
  )

  return (
    <>
      {/* Desktop centered search pill. */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={placeholder}
        className="mx-auto hidden h-9 w-full max-w-md items-center gap-2 rounded-full border border-border bg-muted/50 px-4 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground md:flex">
        <Search className="h-4 w-4 shrink-0" />
        <span className="truncate">{placeholder}</span>
        <Kbd className="ml-auto">⌘K</Kbd>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          mobileSlideFrom="top"
          hideCloseButton
          className="gap-0 p-0 sm:max-w-lg"
          aria-describedby={undefined}
          onOpenAutoFocus={(e) => {
            // DialogContent otherwise focuses the content wrapper; steer
            // focus to the search input so ⌘K users can type immediately.
            e.preventDefault()
            requestAnimationFrame(() => inputRef.current?.focus())
          }}>
          <DialogTitle className="sr-only">{placeholder}</DialogTitle>
          <Command>
            <CommandInput ref={inputRef} placeholder={placeholder} />
            <CommandList>
              <CommandEmpty>{intl.formatMessage({ id: 'search.empty' })}</CommandEmpty>
              {pages.length > 0 ? (
                <CommandGroup heading={intl.formatMessage({ id: 'search.jumpTo' })} className="pb-2">
                  {pages.map((item) => (
                    <CommandItem
                      key={item.slug}
                      value={item.label}
                      onSelect={() => go(item.href)}
                      className="cursor-pointer">
                      {item.label}
                    </CommandItem>
                  ))}
                </CommandGroup>
              ) : null}
            </CommandList>
          </Command>
        </DialogContent>
      </Dialog>
    </>
  )
}
